// containers/FlowStats/UseFlowData.js

/**
 * Generic data hook for FlowStatistic-based dashboards.
 *
 * Works with two grouping modes determined by config.dataSource.groupBy:
 *   - 'machine'    (default) — groups by machineIdentifier, uses power_consumption endpoint
 *   - 'identifier' — groups by identifier, uses flow_data endpoint
 *
 * Drop-in replacement for UsePowerConsumptionData — same return shape.
 */

import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import auth from '../../../config/utils/auth';
import selectWebsocket from '../Websocket/selectors';

export const ALL_MACHINES_ID = '__all__';

export const applyCumulative = (points, cumulativeKeys, prevLastPoint = null) => {
  const result = points.map((p) => ({ ...p }));
  cumulativeKeys.forEach((key) => {
    let sum = prevLastPoint?.[`${key}Cumulative`] ?? 0;
    result.forEach((point) => {
      sum += point[key] ?? 0;
      point[`${key}Cumulative`] = parseFloat(sum.toFixed(2));
    });
  });
  return result;
};

export default function useFlowData({ config: configProp, selectedTab, windowMinutes, customFrom, customTo, refreshKey = 0, liveEnabled = true, staleThresholdMs: staleThresholdProp }) {
  const config = configProp?.default ?? configProp;

  // Stale threshold: config.dataSource.staleThresholdMs > prop > default 30s
  const staleThresholdMs = config?.dataSource?.staleThresholdMs ?? staleThresholdProp ?? 30_000;

  const cumulativeKeys = (config?.graphs ?? []).filter((g) => g.cumulative).map((g) => g.key);
  const liveBufferSize = config?.chartSettings?.liveBufferSize ?? 200;
  const allIds = (config?.machines ?? []).map((m) => m.value);
  const isCustomRange = !!(customFrom && customTo);

  // Determine grouping mode and endpoint from config
  const dataSource = config?.dataSource ?? {};
  const groupBy = dataSource.groupBy ?? 'machine';
  const endpoint = dataSource.endpoint ?? '/api/v1/productionline/flowStatistic/power_consumption/';
  const identifiersParam = dataSource.identifiersParam ?? 'machines';

  // backendIdentifiers: what to send to the backend API (may differ from machines[].value
  // when transformEntry remaps identifiers, e.g. INFEED_FULL+INFEED_EMPTY → INFEED_STATUS)
  const backendIds = dataSource.backendIdentifiers ?? allIds;

  // For machine mode: field names to request from backend
  const fieldsParam = config?.fieldMap ? Object.keys(config.fieldMap).join(',') : '';

  const [allMachineData, setAllMachineData] = useState({});
  const [loading, setLoading] = useState(false);
  const [connectionState, setConnectionState] = useState('idle');
  const [downsampled, setDownsampled] = useState(false);
  const [error, setError] = useState(null);

  const websocketObject = useSelector((state) => selectWebsocket()(state)?.websocket?.websocketObject);

  const configRef = useRef(config);
  const cumulativeKeysRef = useRef(cumulativeKeys);
  const liveBufferSizeRef = useRef(liveBufferSize);
  const allowedIdsRef = useRef(new Set(allIds));
  const lastMessageRef = useRef(null);

  useEffect(() => {
    configRef.current = config;
    cumulativeKeysRef.current = cumulativeKeys;
    liveBufferSizeRef.current = liveBufferSize;
    allowedIdsRef.current = new Set(allIds);
  });

  // ── URL builder ───────────────────────────────────────────────────────────
  const buildUrl = () => {
    const params = new URLSearchParams();
    // Always request all backend identifiers — transformEntry handles the remapping
    params.set(identifiersParam, backendIds.join(','));
    if (fieldsParam) {
      params.set('fields', fieldsParam);
    }
    if (isCustomRange) {
      params.set('from', customFrom);
      params.set('to', customTo);
    } else {
      params.set('window_minutes', windowMinutes ?? 60);
    }
    return `${endpoint}?${params}`;
  };

  // ── Fetch helper ──────────────────────────────────────────────────────────
  const fetchData = () => {
    if (!config?.transformEntry || !backendIds.length) return;
    setLoading(true);
    setError(null);

    axios
      .get(
        buildUrl(),
        dataSource.authenticated
          ? {
              headers: { Authorization: `Bearer ${auth.getToken()}` }
            }
          : undefined
      )
      .then((response) => {
        if (response.status !== 200 || !response.data?.length) {
          setAllMachineData((prev) => {
            const next = { ...prev };
            allIds.forEach((id) => {
              next[id] = [];
            });
            return next;
          });
          setDownsampled(false);
          return;
        }

        setDownsampled(response.data[0]?.downsampled ?? false);

        const grouped = {};
        response.data.forEach((entry) => {
          const point = config.transformEntry(entry);
          // Group key depends on mode — transformEntry may remap identifier
          const gk = groupBy === 'identifier' ? point.identifier : point.machineIdentifier;
          if (!gk) return;
          if (!grouped[gk]) grouped[gk] = [];
          grouped[gk].push(point);
        });

        setAllMachineData((prev) => {
          const next = { ...prev };
          allIds.forEach((id) => {
            next[id] = [];
          });
          Object.entries(grouped).forEach(([gk, points]) => {
            next[gk] = applyCumulative(points, cumulativeKeys);
          });
          return next;
        });
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, [selectedTab, windowMinutes, customFrom, customTo, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!dataSource.pollIntervalMs || isCustomRange || !liveEnabled) return undefined;
    const interval = setInterval(fetchData, dataSource.pollIntervalMs);
    return () => clearInterval(interval);
  }, [dataSource.pollIntervalMs, isCustomRange, liveEnabled, windowMinutes]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── WebSocket — live updates (disabled in custom range mode or when toggled off)
  useEffect(() => {
    if (!websocketObject) return;
    if (isCustomRange || !liveEnabled) {
      setConnectionState('idle');
      return;
    }

    // Only enable live updates for machine-mode (power consumption has WS support)
    // Identifier-mode data sources (laufmeters etc.) don't push via WS yet
    const wsType = dataSource.wsType;
    if (!wsType) {
      setConnectionState('idle');
      return;
    }

    setConnectionState('connected');
    lastMessageRef.current = Date.now();

    const handler = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type !== wsType) return;

        lastMessageRef.current = Date.now();

        const cfg = configRef.current;
        const cumKeys = cumulativeKeysRef.current;
        const bufSize = liveBufferSizeRef.current;
        const allowed = allowedIdsRef.current;
        if (!cfg?.transformEntry) return;

        const entries = Array.isArray(msg.payload) ? msg.payload : [msg.payload];
        entries.forEach((entry) => {
          const newPoint = cfg.transformEntry(entry);
          const gk = groupBy === 'identifier' ? newPoint.identifier : newPoint.machineIdentifier;
          if (!gk || !allowed.has(gk)) return;

          setAllMachineData((prev) => {
            const existing = prev[gk] ?? [];
            const lastPoint = existing[existing.length - 1] ?? null;
            if (lastPoint?.date === newPoint.date) return prev;
            const [withCum] = applyCumulative([newPoint], cumKeys, lastPoint);
            const combined = [...existing, withCum];
            // Only trim if we've grown beyond both the buffer size AND the
            // original fetch size — never trim below what the REST fetch returned
            const maxSize = Math.max(bufSize, existing.length);
            return {
              ...prev,
              [gk]: combined.length > maxSize ? combined.slice(combined.length - maxSize) : combined
            };
          });
        });
      } catch {
        /* ignore non-JSON */
      }
    };

    websocketObject.addEventListener('message', handler);

    const staleCheck = setInterval(() => {
      if (lastMessageRef.current === null) return;
      const age = Date.now() - lastMessageRef.current;
      setConnectionState(age > staleThresholdMs ? 'stale' : 'connected');
    }, 10_000);

    return () => {
      websocketObject.removeEventListener('message', handler);
      clearInterval(staleCheck);
      setConnectionState('idle');
    };
  }, [websocketObject, isCustomRange, liveEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  return { allMachineData, connectionState, loading, downsampled, error };
}
